import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV, CONTACT, IMAGES } from "@/config/content";
import { scrollToId } from "@/lib/scroll";

const Logo = () => (
  <button
    onClick={() => scrollToId("home")}
    data-testid="logo-home-btn"
    className="flex items-center focus-ring"
    aria-label="Straight Sight — go to top"
  >
    <img src={IMAGES.logo} alt="Straight Sight — our focus is your vision" className="h-9 sm:h-10 w-auto" />
  </button>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-[background,box-shadow,padding] duration-500 ${
          scrolled ? "glass soft-shadow py-2.5" : "bg-transparent py-4"
        }`}
        data-testid="site-header"
      >
        <div className="max-site container-px flex items-center justify-between gap-4">
          <Logo />

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => go(n.id)}
                data-testid={`nav-${n.id}`}
                className="px-3 py-2 rounded-full text-sm font-medium text-brand-navy/80 hover:text-brand-teal hover:bg-brand-teal/8 transition-colors focus-ring"
              >
                {n.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => go("contact")}
              data-testid="header-book-btn"
              className="hidden sm:inline-flex items-center rounded-full bg-brand-teal hover:bg-brand-tealdark text-white font-semibold text-sm px-5 py-2.5 shadow-lg shadow-brand-teal/25 transition-colors focus-ring"
            >
              Book a School Program
            </button>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              className="lg:hidden grid place-items-center w-11 h-11 rounded-2xl bg-white/80 text-brand-navy soft-shadow focus-ring"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] lg:hidden"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-brand-navy/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-white p-6 flex flex-col"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
            >
              <div className="flex items-center justify-between mb-8">
                <Logo />
                <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" className="grid place-items-center w-10 h-10 rounded-xl bg-brand-bg focus-ring" aria-label="Close menu">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="flex flex-col gap-1" aria-label="Mobile">
                {NAV.map((n) => (
                  <button key={n.id} onClick={() => go(n.id)} data-testid={`mnav-${n.id}`}
                    className="text-left px-3 py-3 rounded-xl text-base font-medium text-brand-navy hover:bg-brand-teal/8 focus-ring">
                    {n.label}
                  </button>
                ))}
              </nav>
              <div className="mt-auto space-y-3 pt-6">
                <button onClick={() => go("contact")} data-testid="mobile-book-btn"
                  className="w-full rounded-full bg-brand-teal text-white font-semibold py-3 focus-ring">
                  Book a School Program
                </button>
                <a href={`tel:${CONTACT.phoneIntl}`} data-testid="mobile-call-link"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-brand-navy/15 text-brand-navy font-semibold py-3 focus-ring">
                  <Phone className="w-4 h-4" /> {CONTACT.phone}
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
